import { createTheme, CssBaseline, ThemeProvider as MuiThemeProvider } from '@mui/material'
import { enUS, plPL } from '@mui/material/locale'
import { createContext, useContext, useMemo, useState } from 'react'
import { useLocale } from '@/contexts/LocaleContext'
import { affexyTokens } from '@/theme/affexyTokens'
import '@/theme/affexyTheme' // registers the `affexy` theme augmentation

type ThemeContextValue = {
  mode: 'light' | 'dark'
  toggle: () => void
}

const ThemeContext = createContext<ThemeContextValue>({ mode: 'dark', toggle: () => {} })

export function useThemeMode(): ThemeContextValue {
  return useContext(ThemeContext)
}

export default function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setMode] = useState<'light' | 'dark'>('dark')
  const { language } = useLocale()
  const muiLocale = language === 'pl' ? plPL : enUS

  const theme = useMemo(
    () =>
      createTheme(
        {
          affexy: affexyTokens,
          palette: { mode },
          shape: { borderRadius: 10 },
          components: {
            MuiPaper: { defaultProps: { elevation: 0 } },
          },
        },
        muiLocale,
      ),
    [mode, muiLocale],
  )

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, toggle: () => setMode((m) => (m === 'light' ? 'dark' : 'light')) }),
    [mode],
  )

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  )
}
